import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, FormControlLabel, Checkbox, Grid } from '@mui/material';
import { TextFieldComponent } from '../../../../components/common';
import { TipoIdentificacionSelect } from '../../../../components/parametros/TipoIdentificacionSelect';
import { type DuenioFormData, type DuenioFormErrors, type DuenioRequest, type Duenio } from '../types/duenio';
import { useTiposIdentificacion } from '../../../../hooks/useParametric';

interface EditDuenioProps {
  open: boolean;
  duenio: Duenio | null;
  onClose: () => void;
  onSave: (duenio: DuenioRequest) => Promise<void>;
  title: string;
}

const initialFormState: DuenioFormData = {
  nombres: '',
  apellidos: '',
  numeroDocumento: '',
  tipoDocumento: '',
  isActivo: true,
};

export const EditDuenio: React.FC<EditDuenioProps> = ({
  open,
  duenio,
  onClose,
  onSave,
  title,
}) => {
  const [formData, setFormData] = useState<DuenioFormData>(initialFormState);
  const [errors, setErrors] = useState<DuenioFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: tiposIdentificacion } = useTiposIdentificacion();

  useEffect(() => {
    if (open) {
      if (duenio) {
        setFormData({
          nombres: duenio.nombres,
          apellidos: duenio.apellidos,
          numeroDocumento: duenio.numeroDocumento,
          tipoDocumento: duenio.tipoDocumento || '',
          isActivo: duenio.isActivo,
        });
      } else {
        setFormData(initialFormState);
      }
      setErrors({});
    }
  }, [open, duenio]);

  const validateForm = (): boolean => {
    const newErrors: DuenioFormErrors = {};

    if (!formData.nombres.trim()) {
      newErrors.nombres = 'Los nombres son requeridos';
    } else if (formData.nombres.trim().length < 2) {
      newErrors.nombres = 'Los nombres deben tener al menos 2 caracteres';
    }

    if (!formData.apellidos.trim()) {
      newErrors.apellidos = 'Los apellidos son requeridos';
    } else if (formData.apellidos.trim().length < 2) {
      newErrors.apellidos = 'Los apellidos deben tener al menos 2 caracteres';
    }

    if (!formData.numeroDocumento.trim()) {
      newErrors.numeroDocumento = 'El número de documento es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof DuenioFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof DuenioFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, isActivo: event.target.checked }));
  };

  const handleTipoDocumentoChange = (value: string | number) => {
    // Find the tipo by ID and get its name
    const tipo = tiposIdentificacion.find(t => t.idTipoIdentificacion === value);
    const tipoNombre = tipo?.tipo || '';
    setFormData((prev) => ({ ...prev, tipoDocumento: tipoNombre }));
    if (errors.tipoDocumento) {
      setErrors((prev) => ({ ...prev, tipoDocumento: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const request: DuenioRequest = {
        id: duenio?.id,
        nombres: formData.nombres.trim(),
        apellidos: formData.apellidos.trim(),
        numeroDocumento: formData.numeroDocumento.trim(),
        tipoDocumento: formData.tipoDocumento?.trim(),
        isActivo: formData.isActivo,
      };

      await onSave(request);
    } catch (error) {
      console.error('Error saving duenio:', error);
      setErrors({ nombres: 'Error al guardar el dueño' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextFieldComponent
                label="Nombres"
                value={formData.nombres}
                onChange={handleInputChange('nombres')}
                error={errors.nombres}
                required
                fullWidth
                disabled={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextFieldComponent
                label="Apellidos"
                value={formData.apellidos}
                onChange={handleInputChange('apellidos')}
                error={errors.apellidos}
                required
                fullWidth
                disabled={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextFieldComponent
                label="Número de Documento"
                value={formData.numeroDocumento}
                onChange={handleInputChange('numeroDocumento')}
                error={errors.numeroDocumento}
                required
                fullWidth
                disabled={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TipoIdentificacionSelect
                label="Tipo de Documento"
                value={
                  formData.tipoDocumento
                    ? tiposIdentificacion.find(t => t.tipo === formData.tipoDocumento)?.idTipoIdentificacion || ''
                    : ''
                }
                onChange={handleTipoDocumentoChange}
                error={errors.tipoDocumento}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActivo}
                    onChange={handleCheckboxChange}
                    disabled={isSubmitting}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
